import React from 'react';
import { SEO } from '../components/SEO';
import { Check, Users, Award, Target, TrendingUp, Shield, Handshake, BarChart } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import { Link } from 'react-router-dom';

export const WhyPegasus = () => {
  const reasons = [
    {
      title: 'Over a Decade of Regulatory Excellence',
      description: 'Founded in 2014, Pegasus Clinicare has spent over ten years building deep institutional knowledge across FDA, CDSCO, WHO GMP, and international regulatory frameworks. Our expertise is earned through thousands of hours of hands-on regulatory work - not textbooks.',
      icon: <Award size={32} />,
    },
    {
      title: 'Global RLD & Comparator Sourcing Solutions',
      description: 'We are your strategic partner for high-stakes clinical research, providing seamless global procurement of Reference Listed Drugs (RLD) and Comparators from ICH-regulated markets like the USA, EU, and Japan. Our service ensures 100% supply chain authenticity with full pedigree documentation, batch-specific lot matching, and end-to-end GDP-compliant cold chain logistics.',
      icon: <TrendingUp size={32} />,
    },
    {
      title: 'Multidisciplinary Expert Team',
      description: 'Great regulatory consulting requires more than regulatory knowledge. Our team includes pharmacists, pharmaceutical engineers, quality management specialists, facility design experts, and clinical research professionals - providing clients with technical depth that goes far beyond documentation support.',
      icon: <Users size={32} />,
    },
    {
      title: 'Fully Integrated Service Portfolio',
      description: 'We are one of the few firms in India that offers a truly integrated regulatory and compliance service portfolio - spanning regulatory affairs, facility design, GMP advisory, turnkey execution, and drug sourcing. This means fewer handoffs, better coordination, and a more seamless client experience.',
      icon: <Target size={32} />,
    },
    {
      title: 'Proven Track Record with Industry Leaders',
      description: 'Our client portfolio includes some of India\'s and the world\'s most recognized pharmaceutical, medical device, and consumer health brands - including Lupin, 3M, L\'Oreal, ACG, Bilcare, Unichem, and Hikal Ltd. Their continued trust is our most meaningful credential.',
      icon: <BarChart size={32} />,
    },
    {
      title: 'Faster Approvals with Complete Compliance',
      description: 'Our submission-ready approach minimizes query cycles. We prepare complete, accurate, and strategically packaged regulatory submissions the first time - reducing approval timelines and lowering the risk of regulatory hold-ups that cost your business time and money.',
      icon: <TrendingUp size={32} />,
    },
    {
      title: 'Personalized Engagement Model',
      description: 'We do not offer off-the-shelf regulatory services. Every client receives a dedicated engagement team, a customized regulatory roadmap, and transparent project tracking with milestone-based reporting. You always know exactly where your regulatory project stands.',
      icon: <Handshake size={32} />,
    },
    {
      title: 'Commitment to Quality',
      description: 'Our internal quality management system ensures that every deliverable - from regulatory dossiers to facility design documents to sourcing documentation - meets the highest standards of accuracy, consistency, and compliance.',
      icon: <Shield size={32} />,
    },
    {
      title: 'Long-Term Relationship Orientation',
      description: 'Regulatory compliance is not a one-time project - it is an ongoing commitment. Pegasus Clinicare builds long-term partnerships with clients, providing continuous regulatory support through product lifecycle changes, new market entries, regulatory updates, and post-approval activities.',
      icon: <Check size={32} />,
    },
  ];

  return (
    <div className="pt-[72px]">
      <SEO page="why-pegasus" />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/73whohba_05%20Why%20we%2001.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #1570EF 0%, #1570EFee 35%, #1570EFaa 55%, transparent 75%)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white max-w-2xl" >Why Pegasus Clinicare?</h1>
          <p className="text-xl max-w-2xl text-white/90" >
            The Regulatory Partner That Delivers - Not Just Advises.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <p className="text-base sm:text-lg text-center max-w-4xl mx-auto" style={{ color: '#1E293B' }}>
            In the regulatory consulting space, the difference between a good partner and a great one is measurable: faster approvals, fewer queries, fewer surprises, and a team that treats your business objectives as their own. Here is why over 50 leading organizations - from startups to multinationals - trust Pegasus Clinicare with their regulatory success.
          </p>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }}>
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {reasons.map((reason, index) => (
              <div
                key={index}
                data-testid={`reason-${index}`}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#1570EF] rounded-full flex items-center justify-center mb-6">
                  <div className="text-white">{reason.icon}</div>
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#1B3A6B' }}>
                  {reason.title}
                </h3>
                <p className="text-base" style={{ color: '#1E293B' }}>
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1B3A6B] text-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Start Your Partnership with Pegasus
          </h2>
          <Link
            to="/contact"
            data-testid="why-pegasus-cta"
            className="inline-block px-8 py-4 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] hover:scale-105 transition-all duration-200"
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhyPegasus;
